import * as React from 'react';
import ReactQuill from 'react-quill';

import Quill from 'quill';
import Delta from 'quill-delta';

import {NoteRegularIcon} from 'shells/icon/svg/note-regular-icon';
import {IconSection} from 'shells/icon-section/icon-section';
import {getTextValueWithCustomEmbeds} from 'shells/rich-textarea/helpers';
import type {SavedSettingToastState} from 'shells/saved-setting-toast/saved-setting-toast';
import {useDebounce} from 'shells/utils/hooks/use-debounce';
import {useDidMountEffect} from 'shells/utils/hooks/use-did-mount-effect';

import type {GetTeams_teams as Team} from 'nutshell-graphql-types';

import {TextareaMentionable} from '../../../core/textarea-mentionable/textarea-mentionable';
import {mutateQuillHtmlValueForTranscribing} from '../../../speech-to-text';

import {useWatchState} from '../../../../util/hooks';

import styles from './internal-notes.module.css';

type Props = {
    quillDelta: string | null | undefined;
    teams: Team[] | null | undefined;
    onChange: (value: string) => Promise<void>;
    showSave?: boolean;
    cleanupOnUnmount?: boolean;
};

export function NoteSection(props: Props) {
    const {cleanupOnUnmount = true, showSave = true} = props;

    const [noteSaveState, setNoteSaveState] = React.useState<SavedSettingToastState>('initial');
    const [htmlValue, setHtmlValue] = React.useState<string>('');
    const [plaintextValue, setPlaintextValue] = React.useState<string>('');
    const [isTranscribing, setIsTranscribing] = React.useState<boolean>(false);

    const isSaving = React.useRef<boolean>(false);
    const quillRef = React.useRef<ReactQuill>(null);
    const stopTranscribeRef = React.useRef<() => void>(() => {});
    const noteSnapshotRef = React.useRef<{
        html: string;
        plaintext: string;
    }>({
        html: '',
        plaintext: '',
    });
    const lastSavedPlaintextRef = React.useRef<string>('');

    const plaintextValueRef = useWatchState(plaintextValue);
    const debouncedPlaintextValue = useDebounce(plaintextValue, 500);

    const saveNote = React.useCallback(
        (noteValue: string) => {
            if (isSaving.current || lastSavedPlaintextRef.current === noteValue) {
                return;
            }

            isSaving.current = true;
            setNoteSaveState('saving');

            return props
                .onChange(noteValue)
                .then(() => {
                    setNoteSaveState('complete');
                    lastSavedPlaintextRef.current = noteValue;
                })
                .finally(() => {
                    isSaving.current = false;
                });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.onChange]
    );

    useDidMountEffect(() => {
        if (debouncedPlaintextValue) {
            saveNote(debouncedPlaintextValue);
        }
    }, [debouncedPlaintextValue, saveNote]);

    // This useEffect acts as a cleanup function that allows us to commit any
    // unsaved changes when the component unmounts. Useful for saving unsaved
    // changes before the modal closes.
    React.useEffect(() => {
        return () => {
            if (
                lastSavedPlaintextRef.current !== plaintextValueRef.current &&
                !isSaving.current &&
                cleanupOnUnmount
            ) {
                // eslint-disable-next-line react-hooks/exhaustive-deps
                props.onChange(plaintextValueRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (props.quillDelta) {
            const deltaJson = JSON.parse(props.quillDelta);
            const delta = new Delta(deltaJson);
            const quill = new Quill(document.createElement('div'));
            // @ts-expect-error
            quill.setContents(delta);
            setHtmlValue(quill.root.innerHTML);
            const plaintextWithMentions = getTextValueWithCustomEmbeds(quill);
            setPlaintextValue(plaintextWithMentions);
            lastSavedPlaintextRef.current = plaintextWithMentions;
        } else {
            setHtmlValue('');
            lastSavedPlaintextRef.current = '';
            setPlaintextValue('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Editor handle change method
    // @ts-expect-error
    const handleChange = (editorHtmlValue, htmlTextValue) => {
        setHtmlValue(editorHtmlValue);
        setPlaintextValue(htmlTextValue);
    };

    const moveCursorToEndOfCurrentTranscription = (quillEditor: any) => {
        quillEditor.setSelection(quillEditor.getLength(), 0);
    };

    React.useEffect(() => {
        if (isTranscribing && htmlValue.length && quillRef && quillRef.current) {
            moveCursorToEndOfCurrentTranscription(quillRef.current.getEditor());
        }
    }, [htmlValue, quillRef, isTranscribing]);

    const handleBlur = () => {
        handleEndTranscribe();
        saveNote(plaintextValue);
    };

    const handleStartTranscribe = () => {
        noteSnapshotRef.current = {
            html: mutateQuillHtmlValueForTranscribing(htmlValue),
            plaintext: plaintextValue.replace(/\s+$/, ''),
        };
        setIsTranscribing(true);
    };

    // @ts-expect-error
    const handleStopTranscribe = (stopTranscribe) => {
        stopTranscribeRef.current = stopTranscribe;
    };

    const handleTranscribe = (text: string, isFinal: boolean) => {
        if (!noteSnapshotRef.current) return;

        const newHtml = `${noteSnapshotRef.current.html} ${text}`.trim();
        const newPlaintext = `${noteSnapshotRef.current.plaintext} ${text}`.trim();

        if (isFinal) {
            noteSnapshotRef.current = {
                html: newHtml,
                plaintext: newPlaintext,
            };
        }

        handleChange(newHtml, newPlaintext);
    };

    const handleEndTranscribe = () => {
        setIsTranscribing(false);
        stopTranscribeRef.current();
    };

    return (
        <IconSection
            icon={NoteRegularIcon}
            fullWidth={true}
            marginTopValue={2}
            header='Note'
            saveState={showSave ? noteSaveState : undefined}
            setSaveState={showSave ? setNoteSaveState : undefined}
        >
            <div className={styles['notes-section-container']}>
                <TextareaMentionable
                    id='activity-note-textarea'
                    isBodyFocused={isTranscribing}
                    getQuillRef={(ref: HTMLElement | null | undefined) => {
                        // @ts-expect-error
                        quillRef.current = ref;
                    }}
                    isFullHeight={true}
                    overflowVisible={true}
                    placeholder='Log what happened in your activity… @Mention other users to grab their attention, or reference other companies, people, or leads.'
                    value={htmlValue}
                    onChange={handleChange}
                    onTabPropagateToQuill={true}
                    onBlur={handleBlur}
                    includeNotetaker={NutClientConfig.enableNotetaker}
                    notetakerProps={{
                        onStartTranscribe: handleStartTranscribe,
                        handleTranscribe: handleTranscribe,
                        stopTranscribe: handleStopTranscribe,
                        onEndTranscribe: handleEndTranscribe,
                        location: 'activity-modal',
                    }}
                    preloadedTeams={props.teams}
                    fixPlaceholderPosition={true}
                    wrapAnywhere={true}
                    autoFocus={true}
                />
            </div>
        </IconSection>
    );
}
